import parsePhoneNumber from "libphonenumber-js";
import _ from "lodash";
import type { GetServerSidePropsContext } from "next";
import { getSession, signIn } from "next-auth/react";
import { useRouter } from "next/router";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { isEmail } from "validator";

import LicenseRequired from "@calcom/features/ee/common/components/v2/LicenseRequired";
import { isSAMLLoginEnabled } from "@calcom/features/ee/sso/lib/saml";
import { APP_NAME, LOGO } from "@calcom/lib/constants";
import { WEBAPP_URL } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { collectPageParameters, telemetryEventTypes, useTelemetry } from "@calcom/lib/telemetry";
import prisma from "@calcom/prisma";
import type { inferSSRProps } from "@calcom/types/inferSSRProps";
import { Alert, Button, EmailField, HeadSeo, PasswordField, TextField, PhoneInput } from "@calcom/ui";
import { FiArrowRightCircle, FiInfo, FiStar } from "@calcom/ui/components/icon";

import { OnlyRenderOnClient } from "../components/OnlyRenderOnClient";
import { asStringOrNull } from "../lib/asStringOrNull";
import { getCountryByTimezone } from "../lib/getCountryByTimezone";
import { IS_GOOGLE_LOGIN_ENABLED } from "../server/lib/constants";
import { ssrInit } from "../server/lib/ssr";

const URL_REGEX =
  /(^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$|^[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&//=]*)$)/;

type FormValues = {
  username: string;
  email: string;
  password: string;
  passwordcheck: string;
  phoneNumber: string;
};

export default function Signup({ prepopulateFormValues }: inferSSRProps<typeof getServerSideProps>) {
  const { t } = useLocale();
  const router = useRouter();
  const telemetry = useTelemetry();
  const [apiError, setApiError] = useState("");

  const methods = useForm<FormValues>({
    defaultValues: {
      ...(prepopulateFormValues || {}),
      email: prepopulateFormValues?.email ?? prepopulateFormValues?.emailAddress,
    },
  });
  const {
    register,
    formState: { isSubmitting },
    handleSubmit,
  } = methods;

  const handleErrors = async (resp: Response) => {
    if (!resp.ok) {
      const err = await resp.json();
      throw new Error(err.message);
    }
  };

  const signUp: SubmitHandler<FormValues> = async (data) => {
    setApiError("");
    await fetch("/api/auth/signup", {
      body: JSON.stringify({
        ...data,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
      .then(handleErrors)
      .then(async () => {
        telemetry.event(telemetryEventTypes.signup, collectPageParameters());
        await signIn<"credentials">("credentials", {
          ...data,
          callbackUrl: router.query.callbackUrl
            ? `${WEBAPP_URL}/${router.query.callbackUrl}`
            : `${WEBAPP_URL}/getting-started`,
        });
      })
      .catch((err) => {
        setApiError(err.message);
      });
  };

  return (
    <LicenseRequired>
      <div
        className="flex flex-col justify-center min-h-screen py-12 bg-gray-50 sm:px-6 lg:px-8"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true">
        <img className="self-center w-auto h-12 mb-12" src={LOGO} alt={`${APP_NAME} Logo`} />
        <HeadSeo title={t("sign_up")} description={t("sign_up")} />
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-4xl font-black text-center text-gray-900 font-inter">
            {t("create_your_account")}
          </h2>

          <p className="mt-2 font-medium text-center text-gray-900 font-inter">
            {t("create_your_account_subtitle")}
          </p>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="px-4 py-8 mx-2 bg-white shadow sm:rounded-lg sm:px-10">
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(signUp)} className="space-y-6 bg-white">
                {apiError && <Alert severity="error" message={apiError} />}
                <div className="space-y-2">
                  <TextField
                    addOnLeading="https://reserva.doctor/"
                    {...register("username")}
                    required
                  />
                  <EmailField
                    {...register("email")}
                    disabled={prepopulateFormValues?.email}
                    className="disabled:bg-gray-200 disabled:hover:cursor-not-allowed"
                  />

                  <div>
                    <label
                      className="block mb-2 text-sm font-medium leading-none text-gray-700"
                      htmlFor="phoneNumber">
                      Phone Number
                    </label>
                    <OnlyRenderOnClient
                      placeholder={
                        <TextField required id="phoneNumber" placeholder={t("enter_phone_number")} />
                      }>
                      <PhoneInput
                        name="phoneNumber"
                        defaultCountry={getCountryByTimezone()}
                        control={methods.control}
                        placeholder={t("enter_phone_number")}
                        id="phoneNumber"
                        required
                        className={`!rounded-md ${
                          methods.formState.errors.phoneNumber && "!focus:ring-red-700 !border-red-700"
                        }`}
                      />
                    </OnlyRenderOnClient>
                    {methods.formState.errors.phoneNumber && (
                      <div className="flex items-center mt-2 text-sm text-red-700 ">
                        <FiInfo className="w-3 h-3 ltr:mr-2 rtl:ml-2" />
                        <p>{t("phone_validation_error")}</p>
                      </div>
                    )}
                  </div>
                  <PasswordField
                    labelProps={{
                      className: "block text-sm font-medium text-gray-700",
                    }}
                    {...register("password")}
                    className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-black focus:outline-none focus:ring-black sm:text-sm"
                  />
                  <PasswordField
                    label={t("confirm_password")}
                    {...register("passwordcheck", {
                      validate: (value) =>
                        value === methods.watch("password") || (t("error_password_mismatch") as string),
                    })}
                  />
                </div>
                <div className="flex">
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    className="justify-center w-full"
                    EndIcon={FiArrowRightCircle}>
                    {t("create_account")}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
          <div className="flex flex-row justify-center mt-6 space-x-4 align-center">
            <div className="flex -space-x-2 overflow-hidden">
              <img
                className="inline-block w-12 h-12 rounded-full ring-2 ring-white"
                src="https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                alt=""
              />
              <img
                className="inline-block w-12 h-12 rounded-full ring-2 ring-white"
                src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                alt=""
              />
              <img
                className="inline-block w-12 h-12 rounded-full ring-2 ring-white"
                src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80"
                alt=""
              />
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex items-center">
                {[0, 1, 2, 3, 4].map((rating) => (
                  <FiStar
                    key={rating}
                    className="flex-shrink-0 w-5 h-5 text-yellow-400 fill-yellow-400"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <p className="mt-1 font-semibold text-gray-900">Rated 4.8 / 5 stars</p>
            </div>
          </div>
        </div>
      </div>
    </LicenseRequired>
  );
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const session = await getSession(ctx);
  if (session) {
    return {
      redirect: { destination: "/" },
    };
  }

  const ssr = await ssrInit(ctx);
  const token = asStringOrNull(ctx.query.token);

  const phoneNumber =
    typeof ctx.query.phoneNumber === "string" ? parsePhoneNumber(_.trim(ctx.query.phoneNumber)) : undefined;
  const email = typeof ctx.query.email === "string" ? _.trim(ctx.query.email) : undefined;

  const props = {
    isGoogleLoginEnabled: IS_GOOGLE_LOGIN_ENABLED,
    isSAMLLoginEnabled,
    trpcState: ssr.dehydrate(),
    prepopulateFormValues: {} as Record<string, string>,
  };

  if (phoneNumber?.isValid()) {
    props.prepopulateFormValues.phoneNumber = phoneNumber.format("E.164");
  }

  if (email && isEmail(email)) {
    props.prepopulateFormValues.emailAddress = email;
  }

  if (process.env.NEXT_PUBLIC_DISABLE_SIGNUP === "true") {
    return {
      notFound: true,
    };
  }

  // no token given, treat as a normal signup without verification token
  if (!token) {
    return {
      props: JSON.parse(JSON.stringify(props)),
    };
  }

  const verificationToken = await prisma.verificationToken.findUnique({
    where: {
      token,
    },
  });

  if (!verificationToken || verificationToken.expires < new Date()) {
    return {
      notFound: true,
    };
  }

  const existingUser = await prisma.user.findFirst({
    where: {
      AND: [
        {
          email: verificationToken?.identifier,
        },
        {
          emailVerified: {
            not: null,
          },
        },
      ],
    },
  });

  if (existingUser) {
    return {
      redirect: {
        permanent: false,
        destination: "/auth/login?callbackUrl=" + `${WEBAPP_URL}/${ctx.query.callbackUrl}`,
      },
    };
  }

  return {
    props: {
      ...props,
      prepopulateFormValues: {
        email: verificationToken.identifier,
      },
    },
  };
};
